import React, { useState, useEffect } from 'react';
import { Volume2, Mic, RefreshCw, Sparkles, Play, FileText, Diamond } from 'lucide-react';
import { supabase } from '../utils/supabase';

interface GuideSlide {
  english: string;
  persian: string;
  icon: React.ReactNode;
}

interface SparkleParticle {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
}

const guideSlides: GuideSlide[] = [
  {
    english: "You're about to practice a child language learning way.",
    persian: "شما در حال تمرین روش یادگیری زبان کودکان هستید.",
    icon: <Sparkles className="h-16 w-16 text-white" />
  },
  {
    english: "Need to study a text anyway? You can request a custom lesson by just pasting your text. Learn your topic and Dutch at the same time!",
    persian: "به هر حال باید متنی را مطالعه کنید ؟می تونید درخواست درس سفارشی بدید، فقط کافیه متن رو وارد کنید و اون رو به شکل درس تحویل بگیرید . به این شکل میتونید همزمان موضوع خود و هلندی را یاد بگیرید!",
    icon: <FileText className="h-16 w-16 text-white" />
  },
  {
    english: "First, just listen. How much do you understand?",
    persian: "ابتدا فقط گوش دهید. چقدر متوجه می‌شوید؟",
    icon: <Volume2 className="h-16 w-16 text-white" />
  },
  {
    english: "Say the sentences out loud. Repeat until the score is high.",
    persian: "جملات را با صدای بلند بگویید. تا رسیدن به امتیاز بالا تکرار کنید.",
    icon: <Mic className="h-16 w-16 text-white" />
  },
  {
    english: "Now listen to the voice again. You can't believe how much your understanding of the text is improved when you listen to it this time.",
    persian: "حالا دوباره به صدا گوش دهید. باورتان نمی‌شود چقدر درک شما از متن بهبود یافته است.",
    icon: <RefreshCw className="h-16 w-16 text-white" />
  },
  {
    english: "That's it. That's how kids learn language. No brain pressure, no puzzles. Ready? Let's go!",
    persian: "همین. بچه‌ها اینطوری زبان یاد می‌گیرند. بدون فشار ذهنی، بدون معما. آماده‌اید؟ بزن بریم!",
    icon: <Play className="h-16 w-16 text-white" />
  }
];

interface GuideProps {
  onClose: () => void;
  isFirstTime?: boolean;
  userId?: string;
  language?: string;
}

export function Guide({ onClose, isFirstTime = false, userId, language = 'English' }: GuideProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [brightness, setBrightness] = useState(100);
  const [sparkles, setSparkles] = useState<SparkleParticle[]>([]);
  const isPersian = language === 'Persian';
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const onCloseRef = React.useRef(onClose);
  const isPersianRef = React.useRef(isPersian);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  React.useEffect(() => { isPersianRef.current = isPersian; }, [isPersian]);

  const calculateSlideDuration = (slide: GuideSlide): number => {
    const text = isPersian ? slide.persian : slide.english;
    const wordCount = text.split(/\s+/).length;
    const baseTime = 2500;
    const timePerWord = 300;
    const calculatedTime = baseTime + (wordCount * timePerWord);
    const minTime = 3000;
    const maxTime = 12000;
    return Math.max(minTime, Math.min(maxTime, calculatedTime));
  };

  useEffect(() => {
    if (userId) {
      const updateProfile = async () => {
        try {
          const { error } = await supabase
            .from('profiles')
            .update({ has_seen_guide: true })
            .eq('user_id', userId);

          if (error) {
            const { error: upsertError } = await supabase
              .from('profiles')
              .upsert(
                {
                  user_id: userId,
                  has_seen_guide: true
                },
                {
                  onConflict: 'user_id',
                  ignoreDuplicates: false
                }
              );

            if (upsertError) {
              console.error('Error with upsert fallback for has_seen_guide:', upsertError);
            }
          }

          localStorage.setItem('hasSeenGuide', 'true');
        } catch (error) {
          console.error('Error updating has_seen_guide status:', error);
        }
      };

      updateProfile();
    }
  }, [userId]);

  useEffect(() => {
    const bgMusic = new Audio('/arps-of-hope-190257.mp3');
    bgMusic.volume = 0.3;
    bgMusic.loop = true;
    audioRef.current = bgMusic;

    bgMusic.play().catch(error => {
      console.log('Background music autoplay prevented');
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, []);

  useEffect(() => {
    const brightnessInterval = setInterval(() => {
      setBrightness(prev => {
        const change = (Math.random() - 0.5) * 4;
        const newValue = prev + change;
        return Math.max(95, Math.min(105, newValue));
      });
    }, 300);

    return () => clearInterval(brightnessInterval);
  }, []);

  useEffect(() => {
    const generateSparkles = () => {
      const newSparkles: SparkleParticle[] = Array.from({ length: 8 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 12 + 6,
        duration: Math.random() * 4 + 3,
        delay: Math.random() * 3
      }));
      setSparkles(newSparkles);
    };

    generateSparkles();
    const sparkleInterval = setInterval(generateSparkles, 6000);

    return () => clearInterval(sparkleInterval);
  }, []);

  useEffect(() => {
    const currentSlideData = guideSlides[currentSlide];
    const text = isPersianRef.current ? currentSlideData.persian : currentSlideData.english;
    const wordCount = text.split(/\s+/).length;
    const slideDuration = Math.max(3000, Math.min(12000, 2500 + wordCount * 300));

    const timer = setTimeout(() => {
      if (currentSlide < guideSlides.length - 1) {
        setIsVisible(false);
        setTimeout(() => {
          setCurrentSlide(prev => prev + 1);
          setIsVisible(true);
        }, 500);
      } else {
        setIsVisible(false);
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
          onCloseRef.current();
        }, 500);
      }
    }, slideDuration);

    return () => clearTimeout(timer);
  }, [currentSlide]);

  const currentSlideData = guideSlides[currentSlide];
  const text = isPersian ? currentSlideData.persian : currentSlideData.english;

  return (
    <div
      className="fixed inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 flex items-center justify-center z-50 p-4 transition-all duration-300 overflow-hidden"
      style={{ filter: `brightness(${brightness}%)` }}
    >
      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="absolute pointer-events-none"
          style={{
            left: `${sparkle.x}%`,
            top: `${sparkle.y}%`,
            animation: `twinkle ${sparkle.duration}s ease-in-out infinite`,
            animationDelay: `${sparkle.delay}s`
          }}
        >
          <Diamond
            className="text-white"
            style={{
              width: sparkle.size,
              height: sparkle.size,
              filter: 'blur(1px) drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))',
              opacity: 0.4
            }}
          />
        </div>
      ))}

      <div
        className={`transform transition-all duration-500 ease-out relative z-10 ${
          isVisible
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-4'
        }`}
      >
        <div className="text-center px-4 sm:px-8 max-w-4xl">
          <div className="mb-6 sm:mb-8 flex justify-center relative">
            <div className="animate-pulse relative">
              {currentSlideData.icon}
              <div className="absolute -inset-4 bg-white opacity-20 rounded-full blur-xl animate-pulse" />
            </div>
          </div>

          <h1
            className={`text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-relaxed drop-shadow-lg ${
              isPersian ? 'font-vazir' : ''
            }`}
            style={isPersian ? { direction: 'rtl' } : {}}
          >
            {text}
          </h1>

          <div className="mt-8 sm:mt-12 flex justify-center gap-3">
            {guideSlides.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'w-8 bg-white shadow-lg shadow-white/50'
                    : 'w-2 bg-white bg-opacity-40'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes twinkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5) rotate(0deg);
          }
          50% {
            opacity: 0.6;
            transform: scale(1) rotate(90deg);
          }
        }
      `}</style>
    </div>
  );
}
